import { hasHandicapOutcomes, shouldHideHandicapSign, type LineMarket } from "./handicap-outcomes";

/**
 * Betslip's copy of tbd-shared's `formatters/handicap-line-formatters.ts`, which `@ppb/betslip` must not
 * import. Keep the two in step: the betslip title has to name the same line as the market's pebble row.
 */

const QUARTER_OFFSET = 0.25;

const halfLineAt = (sign: string, absHandicap: number, offset: number): string =>
  `${sign}${(absHandicap + offset).toFixed(1)}`;

/**
 * A line as the customer reads it: `+1.0`, `-1.5`, and a quarter line split across its two half lines,
 * `0 & +0.5`. Throws on a handicap that is not a multiple of 0.25.
 */
export const formatHandicapLine = (handicap: number): string => {
  if (handicap === 0) {
    return "0";
  }

  const sign = handicap < 0 ? "-" : "+";
  const absHandicap = Math.abs(handicap);

  switch (absHandicap % 1) {
    case 0:
      return `${sign}${absHandicap}.0`;
    case 0.5:
      return `${sign}${absHandicap}`;
    case 0.25:
      return absHandicap === QUARTER_OFFSET
        ? `0 & ${halfLineAt(sign, absHandicap, QUARTER_OFFSET)}`
        : `${halfLineAt(sign, absHandicap, -QUARTER_OFFSET)} & ${halfLineAt(sign, absHandicap, QUARTER_OFFSET)}`;
    case 0.75:
      return `${halfLineAt(sign, absHandicap, -QUARTER_OFFSET)} & ${halfLineAt(sign, absHandicap, QUARTER_OFFSET)}`;
    default:
      throw new Error("Invalid handicap format");
  }
};

/**
 * A runner's name followed by its own line (`Arsenal +0.5`, `Under 2.5`), on the markets that are bet
 * one line at a time. Goal lines count goals, so they carry no sign. Any other market, or a handicap
 * that is not a valid line, keeps the plain name.
 */
export const formatRunnerNameWithLine = (name: string, handicap: number | undefined, market: LineMarket): string => {
  if (!hasHandicapOutcomes(market) || handicap === undefined) {
    return name;
  }

  try {
    const line = formatHandicapLine(handicap);

    return `${name} ${shouldHideHandicapSign(market.marketType) ? line.replace(/[+-]/g, "") : line}`;
  } catch {
    return name;
  }
};
